import { t } from '@/features/i18n/utils';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Breadcrumb, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton, HeaderUserMenu } from '@/ui/components/header';
import { MainContainer, MainScrollArea } from '@/ui/components/main';
import { procoreConnectionStatusQuery } from '@/features/procore/queries';
import { useProcoreLogin } from '@/features/procore/helpers';
import { useSignOutProcore } from '@/features/procore/mutations';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/ui/components/alert-dialog';
import { Button } from '@/ui/components/button';
import { Spinner } from '@/ui/components/spinner';
import { Icons } from '@/ui/components/icons';
import { Icon } from '@/ui/components/icon';
import { LogoutSquare02Icon } from '@hugeicons-pro/core-stroke-rounded';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/ui/components/empty';

export const Route = createFileRoute('/_authed/settings/procore')({
  component: RouteComponent,
});

function RouteComponent() {
  const login = useProcoreLogin();
  const signOut = useSignOutProcore();

  const { data, isLoading } = useQuery(procoreConnectionStatusQuery());

  if (isLoading) return <Spinner kind="inset" />;

  if (!data?.connected)
    return (
      <Empty>
        <EmptyMedia className="[&_svg]:size-16">
          <Icons.procore />
        </EmptyMedia>
        <EmptyContent className="max-w-lg">
          <EmptyTitle>
            {t({ en: 'IT admin approval required', nb: 'Godkjenning fra IT-admin kreves' })}
          </EmptyTitle>
          <EmptyDescription>
            {t({
              en: 'Your IT admin needs to approve Volve AI as a Procore integration before you can sign in and import files. If this is already approved, sign in below.',
              nb: 'IT-admin må godkjenne Volve AI som en Procore-integrasjon før du kan logge inn og importere filer. Hvis dette allerede er godkjent, logg inn under.',
            })}
          </EmptyDescription>
          <Button kind="with-icon" variant="secondary" onClick={login}>
            <Icons.procore />
            {t({ en: 'Sign in to Procore', nb: 'Logg inn på Procore' })}
          </Button>
        </EmptyContent>
      </Empty>
    );

  return (
    <>
      <Header>
        <HeaderBackButton to="/" />
        <Breadcrumb>
          <BreadcrumbPage>
            <Icons.procore />
            {t({ en: 'Procore', nb: 'Procore' })}
          </BreadcrumbPage>
        </Breadcrumb>
        <HeaderUserMenu />
      </Header>
      <MainScrollArea>
        <MainContainer className="max-w-xl">
          <div>
            <div>
              <h3 className="text-lg font-semibold py-0.75">{data.displayName}</h3>
              <p className="text-sm mb-4 text-muted-foreground">
                {data.email &&
                  t({ en: `Signed in as ${data.email}`, nb: `Logget inn som ${data.email}` })}
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger
                render={
                  <Button kind="with-icon" variant="secondary">
                    <Icon icon={LogoutSquare02Icon} />
                    {t({ en: 'Sign out', nb: 'Logg ut' })}
                  </Button>
                }
              />
              <AlertDialogPopup>
                <AlertDialogTitle>{t({ en: 'Sign out', nb: 'Logg ut' })}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t({
                    en: 'Files imported from Procore will not be deleted.',
                    nb: 'Filer importert fra Procore vil ikke bli slettet.',
                  })}
                </AlertDialogDescription>
                <AlertDialogFooter>
                  <AlertDialogCancel />
                  <Button pending={signOut.isPending} onClick={() => signOut.mutate()}>
                    {t({ en: 'Sign out', nb: 'Logg ut' })}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogPopup>
            </AlertDialog>
          </div>
        </MainContainer>
      </MainScrollArea>
    </>
  );
}
